/*
 * Copyright © 2026 MS IMAGES. All rights reserved.
 * Proprietary and confidential — unauthorized copying, distribution, or use
 * of this file, via any medium, is strictly prohibited. See LICENSE.
 */

/**
 * ============================================================================
 * Webhooks Page - SuperAdmin Portal
 * ============================================================================
 *
 * Manage platform webhooks for event notifications.
 * Create, edit, test, and monitor webhook deliveries.
 *
 * ============================================================================
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Webhook as WebhookIcon,
  Plus,
  RefreshCw,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Clock,
  ExternalLink,
  MoreVertical,
  Play,
  Pencil,
  Trash2,
  RotateCcw,
  ChevronLeft,
  ChevronRight,
  Zap,
  Activity,
  TrendingDown,
} from 'lucide-react';
import { SortableHeader, useSort } from '@/components/ui/SortableHeader';
import {
  useWebhooks,
  useCreateWebhook,
  useUpdateWebhook,
  useDeleteWebhook,
  useTestWebhook,
  useResetCircuit,
  useWebhookStats,
  useAvailableEvents,
} from '@/hooks/api';
import {
  Webhook,
  CreateWebhookRequest,
  UpdateWebhookRequest,
} from '@/types/superadmin.types';
import { useNotification } from '@/hooks/useNotification';
import { isApiError, formatErrorForDisplay } from '@/services/api/errors';
import { ConfirmationDialog, SearchInput } from '@/components/ui';

// Form state type (internal to this component)
interface WebhookFormData {
  name: string;
  url: string;
  events: string[];
  isActive: boolean;
  headers: string;
  retryCount: number;
  timeoutSeconds: number;
}

// Status Badge Component
const StatusBadge: React.FC<{ webhook: Webhook; t: (key: string) => string }> = ({
  webhook,
  t,
}) => {
  if (webhook.consecutiveFailures >= 5) {
    return (
      <span className="status-badge status-circuit-open">
        <AlertTriangle className="w-3 h-3" />
        {t('webhooks.status.circuitOpen')}
      </span>
    );
  }
  if (webhook.isActive) {
    return (
      <span className="status-badge status-active">
        <CheckCircle className="w-3 h-3" />
        {t('webhooks.status.active')}
      </span>
    );
  }
  return (
    <span className="status-badge status-inactive">
      <XCircle className="w-3 h-3" />
      {t('webhooks.status.inactive')}
    </span>
  );
};

// Stat Card Component
const StatCard: React.FC<{
  icon: React.ReactNode;
  value: string | number;
  label: string;
  color: string;
}> = ({ icon, value, label, color }) => (
  <div className="stat-card">
    <div className={`stat-icon ${color}`}>{icon}</div>
    <div className="stat-value">{value}</div>
    <div className="stat-label">{label}</div>
  </div>
);

// Create/Edit Modal Component
const WebhookModal: React.FC<{
  isOpen: boolean;
  webhook?: Webhook | null;
  onClose: () => void;
  onSave: (data: CreateWebhookRequest | UpdateWebhookRequest) => void;
  isSaving: boolean;
  t: (key: string) => string;
}> = ({ isOpen, webhook, onClose, onSave, isSaving, t }) => {
  const [formData, setFormData] = useState<WebhookFormData>({
    name: webhook?.name || '',
    url: webhook?.url || '',
    events: webhook?.events || [],
    isActive: webhook?.isActive ?? true,
    headers: webhook?.headers || '',
    retryCount: webhook?.retryCount || 3,
    timeoutSeconds: webhook?.timeoutSeconds || 30,
  });

  const { data: availableEvents } = useAvailableEvents();

  React.useEffect(() => {
    if (webhook) {
      setFormData({
        name: webhook.name,
        url: webhook.url,
        events: webhook.events || [],
        isActive: webhook.isActive,
        headers: webhook.headers || '',
        retryCount: webhook.retryCount,
        timeoutSeconds: webhook.timeoutSeconds,
      });
    } else {
      setFormData({
        name: '',
        url: '',
        events: [],
        isActive: true,
        headers: '',
        retryCount: 3,
        timeoutSeconds: 30,
      });
    }
  }, [webhook, isOpen]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Convert form data to request format
    if (webhook) {
      // Update - include all fields
      const updateData: UpdateWebhookRequest = {
        name: formData.name,
        url: formData.url,
        events: formData.events,
        isActive: formData.isActive,
        headers: formData.headers || undefined,
        retryCount: formData.retryCount,
        timeoutSeconds: formData.timeoutSeconds,
      };
      onSave(updateData);
    } else {
      // Create - no isActive field
      const createData: CreateWebhookRequest = {
        name: formData.name,
        url: formData.url,
        events: formData.events,
        headers: formData.headers || undefined,
        retryCount: formData.retryCount,
        timeoutSeconds: formData.timeoutSeconds,
      };
      onSave(createData);
    }
  };

  const handleEventToggle = (eventCode: string) => {
    const currentEvents = formData.events;
    const newEvents = currentEvents.includes(eventCode)
      ? currentEvents.filter((e: string) => e !== eventCode)
      : [...currentEvents, eventCode];
    setFormData({ ...formData, events: newEvents });
  };

  const handleAllEventsToggle = () => {
    if (formData.events.includes('*')) {
      setFormData({ ...formData, events: [] });
    } else {
      setFormData({ ...formData, events: ['*'] });
    }
  };

  if (!isOpen) return null;

  const isAllEvents = formData.events.includes('*');

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{webhook ? t('webhooks.createModal.editTitle') : t('webhooks.createModal.title')}</h2>
          <button onClick={onClose} className="modal-close">&times;</button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <div className="form-group">
              <label>{t('webhooks.createModal.name')}</label>
              <input
                type="text"
                value={formData.name}
                onChange={e => setFormData({ ...formData, name: e.target.value })}
                placeholder={t('webhooks.createModal.namePlaceholder')}
                required
              />
            </div>

            <div className="form-group">
              <label>{t('webhooks.createModal.url')}</label>
              <input
                type="url"
                value={formData.url}
                onChange={e => setFormData({ ...formData, url: e.target.value })}
                placeholder={t('webhooks.createModal.urlPlaceholder')}
                required
              />
              <span className="form-hint">{t('webhooks.createModal.urlHelper')}</span>
            </div>

            <div className="form-group">
              <label>{t('webhooks.createModal.events')}</label>
              <div className="events-toggle">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={isAllEvents}
                    onChange={handleAllEventsToggle}
                  />
                  <span>{t('webhooks.createModal.allEvents')}</span>
                </label>
                <span className="form-hint">{t('webhooks.createModal.allEventsHelper')}</span>
              </div>
              {!isAllEvents && availableEvents && (
                <div className="events-grid">
                  {availableEvents.map(event => (
                    <label key={event.code} className="checkbox-label event-checkbox">
                      <input
                        type="checkbox"
                        checked={formData.events.includes(event.code)}
                        onChange={() => handleEventToggle(event.code)}
                      />
                      <span>{event.name || event.code}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>{t('webhooks.createModal.retryCount')}</label>
                <input
                  type="number"
                  min="0"
                  max="10"
                  value={formData.retryCount}
                  onChange={e => setFormData({ ...formData, retryCount: parseInt(e.target.value) || 0 })}
                />
                <span className="form-hint">{t('webhooks.createModal.retryCountHelper')}</span>
              </div>

              <div className="form-group">
                <label>{t('webhooks.createModal.timeoutSeconds')}</label>
                <input
                  type="number"
                  min="5"
                  max="120"
                  value={formData.timeoutSeconds}
                  onChange={e => setFormData({ ...formData, timeoutSeconds: parseInt(e.target.value) || 30 })}
                />
                <span className="form-hint">{t('webhooks.createModal.timeoutSecondsHelper')}</span>
              </div>
            </div>

            <div className="form-group">
              <label>{t('webhooks.createModal.customHeaders')}</label>
              <textarea
                value={formData.headers}
                onChange={e => setFormData({ ...formData, headers: e.target.value })}
                placeholder={t('webhooks.createModal.customHeadersPlaceholder')}
                rows={3}
              />
              <span className="form-hint">{t('webhooks.createModal.customHeadersHelper')}</span>
            </div>

            {webhook && (
              <div className="form-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={formData.isActive}
                    onChange={e => setFormData({ ...formData, isActive: e.target.checked })}
                  />
                  <span>{t('webhooks.createModal.isActive')}</span>
                </label>
                <span className="form-hint">{t('webhooks.createModal.isActiveHelper')}</span>
              </div>
            )}
          </div>

          <div className="modal-footer">
            <button type="button" onClick={onClose} className="btn btn-secondary">
              {t('common.cancel')}
            </button>
            <button type="submit" className="btn btn-primary" disabled={isSaving}>
              {isSaving
                ? (webhook ? t('webhooks.createModal.saving') : t('webhooks.createModal.creating'))
                : (webhook ? t('webhooks.createModal.save') : t('webhooks.createModal.create'))}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Main Webhooks Page
export default function WebhooksPage() {
  const { t } = useTranslation('platform');
  const navigate = useNavigate();
  const { success, error } = useNotification();

  const [page, setPage] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('all');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingWebhook, setEditingWebhook] = useState<Webhook | null>(null);
  const [deleteWebhook, setDeleteWebhook] = useState<Webhook | null>(null);
  const [testWebhookTarget, setTestWebhookTarget] = useState<Webhook | null>(null);
  const [resetCircuitTarget, setResetCircuitTarget] = useState<Webhook | null>(null);
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);

  // Sort state
  const { sortConfig, handleSort, getSortParam } = useSort('name', 'asc');

  // Query hooks
  const {
    data: webhooksData,
    isLoading,
    refetch,
  } = useWebhooks({
    page,
    size: 20,
    active: statusFilter === 'all' ? undefined : statusFilter === 'active',
    search: searchQuery || undefined,
    sort: getSortParam(),
  });

  const { data: stats } = useWebhookStats();
  const createMutation = useCreateWebhook();
  const updateMutation = useUpdateWebhook();
  const deleteMutation = useDeleteWebhook();
  const testMutation = useTestWebhook();
  const resetCircuitMutation = useResetCircuit();

  const webhooks = webhooksData?.content || [];
  const totalPages = webhooksData?.page?.totalPages || 1;
  const totalElements = webhooksData?.page?.totalElements || 0;

  // Handlers
  const handleCreate = () => {
    setEditingWebhook(null);
    setIsModalOpen(true);
  };

  const handleEdit = (webhook: Webhook) => {
    setEditingWebhook(webhook);
    setIsModalOpen(true);
    setOpenMenuId(null);
  };

  const handleSave = async (data: CreateWebhookRequest | UpdateWebhookRequest) => {
    try {
      if (editingWebhook) {
        await updateMutation.mutateAsync({ id: editingWebhook.id, data: data as UpdateWebhookRequest });
        success(t('webhooks.messages.updateSuccess'));
      } else {
        await createMutation.mutateAsync(data as CreateWebhookRequest);
        success(t('webhooks.messages.createSuccess'));
      }
      setIsModalOpen(false);
      setEditingWebhook(null);
    } catch (err) {
      const fallback = editingWebhook ? t('webhooks.messages.updateError') : t('webhooks.messages.createError');
      const { message } = isApiError(err) ? formatErrorForDisplay(err, t) : { message: fallback };
      error(message);
    }
  };

  const handleDelete = async () => {
    if (!deleteWebhook) return;
    try {
      await deleteMutation.mutateAsync(deleteWebhook.id);
      success(t('webhooks.messages.deleteSuccess'));
      setDeleteWebhook(null);
    } catch (err) {
      const { message } = isApiError(err) ? formatErrorForDisplay(err, t) : { message: t('webhooks.messages.deleteError') };
      error(message);
    }
  };

  const handleTest = async () => {
    if (!testWebhookTarget) return;
    try {
      await testMutation.mutateAsync(testWebhookTarget.id);
      success(t('webhooks.messages.testSuccess'));
      setTestWebhookTarget(null);
    } catch (err) {
      const { message } = isApiError(err) ? formatErrorForDisplay(err, t) : { message: t('webhooks.messages.testError') };
      error(message);
    }
  };

  const handleResetCircuit = async () => {
    if (!resetCircuitTarget) return;
    try {
      await resetCircuitMutation.mutateAsync(resetCircuitTarget.id);
      success(t('webhooks.messages.resetCircuitSuccess'));
      setResetCircuitTarget(null);
    } catch (err) {
      const { message } = isApiError(err) ? formatErrorForDisplay(err, t) : { message: t('webhooks.messages.resetCircuitError') };
      error(message);
    }
  };

  const formatDate = (dateStr: string | null | undefined) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatEvents = (events: string[]) => {
    if (!events || events.length === 0) return '-';
    if (events.includes('*')) return 'All Events';
    if (events.length <= 2) return events.join(', ');
    return `${events.slice(0, 2).join(', ')} +${events.length - 2}`;
  };

  return (
    <div className="webhooks-page">
      {/* Header */}
      <div className="page-header">
        <div>
          <h1 className="page-title">
            <WebhookIcon className="w-6 h-6" /> {t('webhooks.title')}
          </h1>
          <p className="page-subtitle">{t('webhooks.subtitle')}</p>
        </div>
        <div className="header-actions">
          <button onClick={() => refetch()} className="btn btn-secondary">
            <RefreshCw className="w-4 h-4" />
            {t('common.refresh')}
          </button>
          <button onClick={handleCreate} className="btn btn-primary">
            <Plus className="w-4 h-4" />
            {t('webhooks.addWebhook')}
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="stats-grid">
        <StatCard
          icon={<WebhookIcon className="w-5 h-5" />}
          value={stats?.total || 0}
          label={t('webhooks.stats.totalWebhooks')}
          color="stat-icon-blue"
        />
        <StatCard
          icon={<Zap className="w-5 h-5" />}
          value={stats?.active || 0}
          label={t('webhooks.stats.activeWebhooks')}
          color="stat-icon-green"
        />
        <StatCard
          icon={<Activity className="w-5 h-5" />}
          value={stats?.inactive || 0}
          label={t('webhooks.stats.deliveriesLast24h')}
          color="stat-icon-purple"
        />
        <StatCard
          icon={<TrendingDown className="w-5 h-5" />}
          value={stats?.circuitOpen || 0}
          label={t('webhooks.stats.failureRate')}
          color="stat-icon-red"
        />
      </div>

      {/* Filters */}
      <div className="filters-bar">
        <SearchInput
          value={searchQuery}
          onChange={setSearchQuery}
          placeholder={t('webhooks.searchPlaceholder')}
          className="search-wrapper"
        />
        <select
          value={statusFilter}
          onChange={e => { setStatusFilter(e.target.value as 'all' | 'active' | 'inactive'); setPage(0); }}
          className="filter-select"
        >
          <option value="all">{t('webhooks.filters.allStatus')}</option>
          <option value="active">{t('webhooks.filters.active')}</option>
          <option value="inactive">{t('webhooks.filters.inactive')}</option>
        </select>
      </div>

      {/* Webhooks Table */}
      <div className="webhooks-table-card">
        <div className="table-header">
          <span className="table-col col-name">
            <SortableHeader
              columnKey="name"
              label={t('webhooks.table.name')}
              sortConfig={sortConfig}
              onSort={handleSort}
            />
          </span>
          <span className="table-col col-url">
            <SortableHeader
              columnKey="url"
              label={t('webhooks.table.url')}
              sortConfig={sortConfig}
              onSort={handleSort}
            />
          </span>
          <span className="table-col col-events">{t('webhooks.table.events')}</span>
          <span className="table-col col-status">
            <SortableHeader
              columnKey="isActive"
              label={t('webhooks.table.status')}
              sortConfig={sortConfig}
              onSort={handleSort}
            />
          </span>
          <span className="table-col col-last-triggered">
            <SortableHeader
              columnKey="lastTriggeredAt"
              label={t('webhooks.table.lastTriggered')}
              sortConfig={sortConfig}
              onSort={handleSort}
            />
          </span>
          <span className="table-col col-actions">{t('webhooks.table.actions')}</span>
        </div>

        <div className="table-body">
          {isLoading ? (
            <div className="table-loading">
              <div className="spinner" />
              <span>{t('common.loading')}</span>
            </div>
          ) : webhooks.length === 0 ? (
            <div className="table-empty">
              <WebhookIcon className="w-12 h-12" />
              <p>{t('webhooks.empty')}</p>
              <span>{t('webhooks.emptyDescription')}</span>
              <button onClick={handleCreate} className="btn btn-primary mt-4">
                <Plus className="w-4 h-4" />
                {t('webhooks.addWebhook')}
              </button>
            </div>
          ) : (
            webhooks.map((webhook: Webhook) => (
              <div key={webhook.id} className="table-row">
                <div className="table-col col-name">
                  <span className="webhook-name">{webhook.name}</span>
                </div>
                <div className="table-col col-url">
                  <span className="webhook-url" title={webhook.url}>
                    <ExternalLink className="w-3.5 h-3.5" />
                    {webhook.url}
                  </span>
                </div>
                <div className="table-col col-events">
                  <span className="events-badge">{formatEvents(webhook.events)}</span>
                </div>
                <div className="table-col col-status">
                  <StatusBadge webhook={webhook} t={t} />
                </div>
                <div className="table-col col-last-triggered">
                  <Clock className="w-3.5 h-3.5 text-muted" />
                  <span>{formatDate(webhook.lastTriggeredAt)}</span>
                </div>
                <div className="table-col col-actions">
                  <button
                    onClick={() => navigate(`/superadmin/webhooks/${webhook.id}`)}
                    className="action-btn"
                    title={t('webhooks.actions.viewDeliveries')}
                  >
                    <Activity className="w-4 h-4" />
                  </button>
                  <div className="action-menu-container">
                    <button
                      onClick={() => setOpenMenuId(openMenuId === webhook.id ? null : webhook.id)}
                      className="action-btn"
                    >
                      <MoreVertical className="w-4 h-4" />
                    </button>
                    {openMenuId === webhook.id && (
                      <div className="action-menu">
                        <button onClick={() => { setTestWebhookTarget(webhook); setOpenMenuId(null); }}>
                          <Play className="w-4 h-4" />
                          {t('webhooks.actions.test')}
                        </button>
                        <button onClick={() => handleEdit(webhook)}>
                          <Pencil className="w-4 h-4" />
                          {t('webhooks.actions.edit')}
                        </button>
                        {webhook.consecutiveFailures >= 5 && (
                          <button onClick={() => { setResetCircuitTarget(webhook); setOpenMenuId(null); }}>
                            <RotateCcw className="w-4 h-4" />
                            {t('webhooks.actions.resetCircuit')}
                          </button>
                        )}
                        <button
                          onClick={() => { setDeleteWebhook(webhook); setOpenMenuId(null); }}
                          className="danger"
                        >
                          <Trash2 className="w-4 h-4" />
                          {t('webhooks.actions.delete')}
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="table-pagination">
            <span className="pagination-info">
              {t('webhooks.showing', { count: webhooks.length, total: totalElements })}
            </span>
            <div className="pagination-buttons">
              <button
                onClick={() => setPage(p => Math.max(0, p - 1))}
                disabled={page === 0}
                className="pagination-btn"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <span className="page-indicator">{page + 1} / {totalPages}</span>
              <button
                onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
                disabled={page >= totalPages - 1}
                className="pagination-btn"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Create/Edit Modal */}
      <WebhookModal
        isOpen={isModalOpen}
        webhook={editingWebhook}
        onClose={() => { setIsModalOpen(false); setEditingWebhook(null); }}
        onSave={handleSave}
        isSaving={createMutation.isPending || updateMutation.isPending}
        t={t}
      />

      {/* Delete Confirmation */}
      <ConfirmationDialog
        isOpen={!!deleteWebhook}
        title={t('webhooks.deleteDialog.title')}
        message={t('webhooks.deleteDialog.message')}
        variant="danger"
        onConfirm={handleDelete}
        onCancel={() => setDeleteWebhook(null)}
        isLoading={deleteMutation.isPending}
        confirmText={t('webhooks.deleteDialog.confirm')}
        details={deleteWebhook ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <div><strong>{t('webhooks.deleteDialog.webhook')}:</strong> {deleteWebhook.name}</div>
            <div><strong>{t('webhooks.deleteDialog.url')}:</strong> {deleteWebhook.url}</div>
          </div>
        ) : undefined}
      />

      {/* Test Confirmation */}
      <ConfirmationDialog
        isOpen={!!testWebhookTarget}
        title={t('webhooks.testDialog.title')}
        message={t('webhooks.testDialog.message')}
        variant="confirm"
        onConfirm={handleTest}
        onCancel={() => setTestWebhookTarget(null)}
        isLoading={testMutation.isPending}
        confirmText={t('webhooks.testDialog.confirm')}
      />

      {/* Reset Circuit Confirmation */}
      <ConfirmationDialog
        isOpen={!!resetCircuitTarget}
        title={t('webhooks.resetCircuitDialog.title')}
        message={t('webhooks.resetCircuitDialog.message')}
        variant="warning"
        onConfirm={handleResetCircuit}
        onCancel={() => setResetCircuitTarget(null)}
        isLoading={resetCircuitMutation.isPending}
        confirmText={t('webhooks.resetCircuitDialog.confirm')}
      />

      <style>{webhooksPageStyles}</style>
    </div>
  );
}

// ============================================================================
// STYLES
// ============================================================================

const webhooksPageStyles = `
  .webhooks-page {
    display: flex;
    flex-direction: column;
    gap: 24px;
  }

  /* Page Header */
  .page-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  .page-title {
    display: flex;
    align-items: center;
    gap: 8px;
    font-family: var(--font-heading);
    font-size: 22px;
    font-weight: 700;
    color: var(--color-text-primary);
  }

  .page-subtitle {
    font-size: 13px;
    color: var(--color-text-secondary);
    margin-top: 4px;
  }

  .header-actions {
    display: flex;
    gap: 12px;
  }

  /* Button */
  .btn {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 0 16px;
    height: 40px;
    font-size: 13px;
    font-weight: 500;
    border-radius: 8px;
    border: none;
    cursor: pointer;
    transition: all 0.15s ease;
  }

  .btn-primary {
    background: var(--color-brand-primary);
    color: white;
  }

  .btn-primary:hover {
    background: var(--color-brand-primary-hover);
  }

  .btn-secondary {
    background: var(--color-bg-secondary);
    border: 1px solid var(--color-border-subtle);
    color: var(--color-text-primary);
  }

  .btn-secondary:hover {
    background: var(--color-bg-hover);
  }

  .btn:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  .mt-4 {
    margin-top: 16px;
  }

  /* Stats Grid */
  .stats-grid {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 16px;
  }

  .stat-card {
    background: var(--color-bg-secondary);
    border: 1px solid var(--color-border-subtle);
    border-radius: 12px;
    padding: 16px;
  }

  .stat-icon {
    width: 40px;
    height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 10px;
    margin-bottom: 12px;
  }

  .stat-icon-blue { background: rgba(59, 130, 246, 0.15); color: #3B82F6; }
  .stat-icon-green { background: rgba(16, 185, 129, 0.15); color: #10B981; }
  .stat-icon-red { background: rgba(239, 68, 68, 0.15); color: #EF4444; }
  .stat-icon-purple { background: rgba(139, 92, 246, 0.15); color: #8B5CF6; }

  .stat-value {
    font-size: 24px;
    font-weight: 700;
    font-family: var(--font-mono);
    color: var(--color-text-primary);
  }

  .stat-label {
    font-size: 12px;
    color: var(--color-text-muted);
    margin-top: 4px;
  }

  /* Filters Bar */
  .filters-bar {
    display: flex;
    gap: 16px;
    align-items: center;
  }

  .search-wrapper {
    flex: 1;
    max-width: 400px;
  }

  .filter-select {
    height: 40px;
    padding: 0 12px;
    background: var(--color-bg-secondary);
    border: 1px solid var(--color-border-subtle);
    border-radius: 8px;
    font-size: 13px;
    color: var(--color-text-primary);
    outline: none;
    min-width: 150px;
  }

  /* Webhooks Table */
  .webhooks-table-card {
    background: var(--color-bg-secondary);
    border: 1px solid var(--color-border-subtle);
    border-radius: 12px;
    overflow: hidden;
  }

  .table-header {
    display: grid;
    grid-template-columns: 180px 1fr 150px 120px 140px 100px;
    gap: 12px;
    padding: 12px 20px;
    background: var(--color-bg-primary);
    border-bottom: 1px solid var(--color-border-subtle);
  }

  .table-header .table-col {
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: var(--color-text-muted);
  }

  .table-body {
    max-height: 600px;
    overflow-y: auto;
  }

  .table-row {
    display: grid;
    grid-template-columns: 180px 1fr 150px 120px 140px 100px;
    gap: 12px;
    padding: 14px 20px;
    border-bottom: 1px solid var(--color-border-subtle);
    align-items: center;
    transition: background 0.1s ease;
  }

  .table-row:last-child {
    border-bottom: none;
  }

  .table-row:hover {
    background: var(--color-bg-hover);
  }

  .table-col {
    font-size: 13px;
    color: var(--color-text-primary);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .webhook-name {
    font-weight: 500;
  }

  .webhook-url {
    display: flex;
    align-items: center;
    gap: 6px;
    color: var(--color-text-secondary);
    font-family: var(--font-mono);
    font-size: 12px;
  }

  .events-badge {
    display: inline-block;
    padding: 4px 8px;
    background: var(--color-bg-primary);
    border-radius: 6px;
    font-size: 11px;
    color: var(--color-text-secondary);
  }

  .col-last-triggered {
    display: flex;
    align-items: center;
    gap: 6px;
    color: var(--color-text-secondary);
    font-size: 12px;
  }

  .col-actions {
    display: flex;
    align-items: center;
    gap: 4px;
    justify-content: flex-end;
  }

  .action-btn {
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: transparent;
    border: none;
    border-radius: 6px;
    color: var(--color-text-secondary);
    cursor: pointer;
    transition: all 0.15s ease;
  }

  .action-btn:hover {
    background: var(--color-bg-hover);
    color: var(--color-text-primary);
  }

  .action-menu-container {
    position: relative;
  }

  .action-menu {
    position: absolute;
    top: 100%;
    right: 0;
    margin-top: 4px;
    background: var(--color-bg-secondary);
    border: 1px solid var(--color-border-subtle);
    border-radius: 8px;
    box-shadow: 0 10px 40px rgba(0, 0, 0, 0.15);
    min-width: 160px;
    z-index: 100;
    overflow: hidden;
  }

  .action-menu button {
    display: flex;
    align-items: center;
    gap: 8px;
    width: 100%;
    padding: 10px 14px;
    background: transparent;
    border: none;
    font-size: 13px;
    color: var(--color-text-primary);
    cursor: pointer;
    transition: background 0.1s ease;
    text-align: left;
  }

  .action-menu button:hover {
    background: var(--color-bg-hover);
  }

  .action-menu button.danger {
    color: #EF4444;
  }

  /* Status Badges */
  .status-badge {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 4px 10px;
    border-radius: 20px;
    font-size: 11px;
    font-weight: 600;
  }

  .status-active {
    background: rgba(16, 185, 129, 0.15);
    color: #10B981;
  }

  .status-inactive {
    background: rgba(107, 114, 128, 0.15);
    color: #6B7280;
  }

  .status-circuit-open {
    background: rgba(239, 68, 68, 0.15);
    color: #EF4444;
  }

  .text-muted {
    color: var(--color-text-muted);
  }

  /* Loading & Empty States */
  .table-loading {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 64px;
    gap: 16px;
    color: var(--color-text-muted);
  }

  .spinner {
    width: 32px;
    height: 32px;
    border: 3px solid var(--color-border-subtle);
    border-top-color: var(--color-brand-primary);
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
  }

  @keyframes spin {
    to { transform: rotate(360deg); }
  }

  .table-empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 64px;
    color: var(--color-text-muted);
    text-align: center;
  }

  .table-empty p {
    margin-top: 16px;
    font-size: 15px;
    font-weight: 500;
    color: var(--color-text-secondary);
  }

  .table-empty span {
    font-size: 13px;
    margin-top: 4px;
  }

  /* Pagination */
  .table-pagination {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 12px 20px;
    border-top: 1px solid var(--color-border-subtle);
  }

  .pagination-info {
    font-size: 13px;
    color: var(--color-text-muted);
  }

  .pagination-buttons {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .pagination-btn {
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: var(--color-bg-primary);
    border: 1px solid var(--color-border-subtle);
    border-radius: 6px;
    color: var(--color-text-primary);
    cursor: pointer;
    transition: all 0.15s ease;
  }

  .pagination-btn:hover:not(:disabled) {
    background: var(--color-bg-hover);
  }

  .pagination-btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  .page-indicator {
    font-size: 13px;
    color: var(--color-text-secondary);
    min-width: 60px;
    text-align: center;
  }

  /* Modal */
  .modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
    padding: 24px;
  }

  .modal-content {
    background: var(--color-bg-secondary);
    border: 1px solid var(--color-border-subtle);
    border-radius: 16px;
    width: 100%;
    max-width: 600px;
    max-height: 90vh;
    overflow: hidden;
    display: flex;
    flex-direction: column;
  }

  .modal-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 20px 24px;
    border-bottom: 1px solid var(--color-border-subtle);
  }

  .modal-header h2 {
    font-size: 18px;
    font-weight: 600;
    color: var(--color-text-primary);
  }

  .modal-close {
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: transparent;
    border: none;
    font-size: 24px;
    color: var(--color-text-muted);
    cursor: pointer;
    border-radius: 6px;
  }

  .modal-close:hover {
    background: var(--color-bg-hover);
    color: var(--color-text-primary);
  }

  .modal-body {
    padding: 24px;
    overflow-y: auto;
  }

  .modal-footer {
    display: flex;
    justify-content: flex-end;
    gap: 12px;
    padding: 16px 24px;
    border-top: 1px solid var(--color-border-subtle);
  }

  /* Form Elements */
  .form-group {
    margin-bottom: 20px;
  }

  .form-group label {
    display: block;
    font-size: 13px;
    font-weight: 500;
    color: var(--color-text-primary);
    margin-bottom: 6px;
  }

  .form-group input,
  .form-group textarea,
  .form-group select {
    width: 100%;
    padding: 10px 12px;
    background: var(--color-bg-primary);
    border: 1px solid var(--color-border-subtle);
    border-radius: 8px;
    font-size: 13px;
    color: var(--color-text-primary);
    outline: none;
    transition: border-color 0.15s ease;
  }

  .form-group input:focus,
  .form-group textarea:focus,
  .form-group select:focus {
    border-color: var(--color-brand-primary);
  }

  .form-group textarea {
    resize: vertical;
    min-height: 80px;
  }

  .form-hint {
    display: block;
    font-size: 11px;
    color: var(--color-text-muted);
    margin-top: 4px;
  }

  .form-row {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 16px;
  }

  .checkbox-label {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    cursor: pointer;
    font-size: 13px;
    color: var(--color-text-primary);
  }

  .checkbox-label input[type="checkbox"] {
    width: 16px;
    height: 16px;
    accent-color: var(--color-brand-primary);
  }

  .events-toggle {
    margin-bottom: 12px;
  }

  .events-grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 8px;
    max-height: 200px;
    overflow-y: auto;
    padding: 12px;
    background: var(--color-bg-primary);
    border: 1px solid var(--color-border-subtle);
    border-radius: 8px;
  }

  .event-checkbox {
    font-size: 12px;
    padding: 4px 0;
  }

  /* Responsive */
  @media (max-width: 1200px) {
    .stats-grid {
      grid-template-columns: repeat(2, 1fr);
    }

    .table-header,
    .table-row {
      grid-template-columns: 150px 1fr 120px 100px;
    }

    .col-events,
    .col-last-triggered {
      display: none;
    }
  }

  @media (max-width: 768px) {
    .filters-bar {
      flex-direction: column;
    }

    .search-wrapper {
      max-width: none;
      width: 100%;
    }

    .filter-select {
      width: 100%;
    }

    .table-header,
    .table-row {
      grid-template-columns: 1fr 100px 80px;
    }

    .col-url {
      display: none;
    }

    .form-row {
      grid-template-columns: 1fr;
    }

    .events-grid {
      grid-template-columns: 1fr;
    }
  }
`;
